import React, { useState } from 'react';
import { Mui } from '@leap4/frontend';
import { ITrackedLine, IUpdateList, statusLabel } from './appAdsLines.interface';
import { dateToStringFormat } from '../../utils/functions';
import { readSheetRows, readTextFile } from './readSheet';
import { IRowIssue, rejectionFromError } from './appAdsLines.api';
import { RowIssueList } from './rowIssues';

const {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItem,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
} = Mui;

// ============================= add / edit one line =============================

interface LineFormModalProps {
    open: boolean;
    line?: ITrackedLine | null;
    onClose: () => void;
    onSave: (row: {
        domain: string;
        pubId: string;
        relationship: string;
        certId?: string;
        demand?: string;
    }) => Promise<void>;
    title?: string;
    actionLabel?: string;
    /**
     * Removal treats relationship as a disambiguator, not part of the identity: leaving it
     * blank means "whichever way it's listed". Adding can't work that way - a line without a
     * relationship isn't a line - so the field is required in that direction only.
     */
    relationshipOptional?: boolean;
    /** Cert ID plays no part in matching, so the removal form doesn't offer it. */
    hideCertId?: boolean;
    /** Demand is an annotation on a line we keep, so the removal form has no use for it. */
    hideDemand?: boolean;
}

export const LineFormModal: React.FC<LineFormModalProps> = ({
    open,
    line,
    onClose,
    onSave,
    title,
    actionLabel = 'Save',
    relationshipOptional = false,
    hideCertId = false,
    hideDemand = false,
}) => {
    const [domain, setDomain] = useState(line?.domain || '');
    const [pubId, setPubId] = useState(line?.pubId || '');
    const [relationship, setRelationship] = useState(line?.relationship || (relationshipOptional ? '' : 'DIRECT'));
    const [certId, setCertId] = useState(line?.certId || '');
    const [demand, setDemand] = useState(line?.demand || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Reset when the modal reopens for a different line.
    React.useEffect(() => {
        if (open) {
            setDomain(line?.domain || '');
            setPubId(line?.pubId || '');
            setRelationship(line?.relationship || (relationshipOptional ? '' : 'DIRECT'));
            setCertId(line?.certId || '');
            // Falls back to the generator's value so editing a generated line starts from what
            // it already implies rather than blank - saving then makes it ad-ops' own.
            setDemand(line?.demand || line?.sourceDescription || '');
            setError('');
        }
    }, [open, line, relationshipOptional]);

    const submit = async () => {
        if (!domain.trim() || !pubId.trim()) {
            setError('Domain and publisher ID are both required.');
            return;
        }
        if (!relationshipOptional && !relationship.trim()) {
            setError('Relationship is required.');
            return;
        }
        try {
            setBusy(true);
            setError('');
            await onSave({
                domain: domain.trim(),
                pubId: pubId.trim(),
                relationship: relationship.trim().toUpperCase(),
                certId: certId.trim() || undefined,
                demand: hideDemand ? undefined : demand.trim() || undefined,
            });
            onClose();
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title || (line ? 'Edit line' : 'Add line')}</DialogTitle>
            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2} pt={1}>
                    <TextField label="Ad system domain" value={domain} onChange={(e) => setDomain(e.target.value)} required />
                    <TextField
                        label="Pub ID"
                        value={pubId}
                        onChange={(e) => setPubId(e.target.value)}
                        required
                        helperText="The account ID with that ad system"
                    />
                    <TextField
                        label={relationshipOptional ? 'Relationship (optional)' : 'Relationship'}
                        value={relationship}
                        onChange={(e) => setRelationship(e.target.value)}
                        required={!relationshipOptional}
                        helperText={
                            relationshipOptional
                                ? 'Leave blank to match the line however it is listed'
                                : 'Usually DIRECT or RESELLER'
                        }
                    />
                    {!hideCertId && (
                        <TextField
                            label="Cert ID (optional)"
                            value={certId}
                            onChange={(e) => setCertId(e.target.value)}
                            helperText="Stored and shown for reference; it isn't used when matching"
                        />
                    )}
                    {!hideDemand && (
                        <TextField
                            label="Demand (optional)"
                            value={demand}
                            onChange={(e) => setDemand(e.target.value)}
                            helperText="Which demand this line is for. Free text — it's your note, not a fixed list."
                        />
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy}>
                    {actionLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ============================= paste lines =============================

/**
 * What a dry run reports back, whichever direction it's going.
 *
 * `understood` is "rows we could read and would act on" - added for an import, matched for a
 * removal. Normalising here is what lets one component serve both, which is the point: adding
 * and removing lines have to behave identically, so they can't be two implementations that
 * drift apart.
 */
export interface ITextPreview {
    rowsRead: number;
    understood: number;
    issues: IRowIssue[];
    /** The direction-specific half of the sentence, e.g. "3 would be added, 1 already present." */
    detail?: string;
}

interface PasteLinesModalProps {
    open: boolean;
    title?: string;
    helperText?: string;
    placeholder?: string;
    /** Label for the commit button - "Import" when adding, "Remove" when removing. */
    actionLabel?: string;
    /** Dry run. Nothing is written until the user confirms. */
    onPreview: (text: string) => Promise<ITextPreview>;
    onClose: () => void;
    /** Shape differs by direction ({added, skipped} vs {removed, notFound}), hence `any`. */
    onSubmit: (text: string) => Promise<any>;
    /** Sentence shown once a dry run passes. */
    describePreview?: (preview: ITextPreview) => string;
    /** Sentence shown once the write completes. */
    describeResult?: (result: any) => string;
}

/**
 * Paste lines, with the same check-before-commit the file upload gets.
 *
 * Previously this submitted directly, so a typo in one line was silently dropped and the count
 * quietly came back lower than expected. Now the paste is validated first, and a single bad row
 * blocks the whole import rather than importing around it - the user's list is their intent,
 * and guessing which parts of it to honour is worse than asking them to fix it.
 */
export const PasteLinesModal: React.FC<PasteLinesModalProps> = ({
    open,
    title = 'Paste lines',
    helperText = 'One app-ads.txt line per row: domain, publisher ID, relationship, and optionally a cert ID. Blank lines and # comments are ignored.',
    placeholder = 'pubmatic.com, 161599, DIRECT, 5d62403b186f2ace\nconversantmedia.com, 100353, RESELLER',
    actionLabel = 'Import',
    onPreview,
    onClose,
    onSubmit,
    describePreview = (p) => `${p.rowsRead} line(s) read, all readable.${p.detail ? ` ${p.detail}` : ''}`,
    describeResult = (r) => `Added ${r.added}. Skipped ${r.skipped} already present.`,
}) => {
    const [text, setText] = useState('');
    const [preview, setPreview] = useState<ITextPreview | null>(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [issues, setIssues] = useState<IRowIssue[]>([]);
    const [error, setError] = useState('');

    React.useEffect(() => {
        if (open) {
            setText('');
            setPreview(null);
            setResult(null);
            setIssues([]);
            setError('');
        }
    }, [open]);

    // Re-checking on every keystroke would be a request per character, so the check happens
    // when the field loses focus and again on submit.
    const check = async () => {
        if (!text.trim()) {
            setPreview(null);
            return;
        }
        try {
            setBusy(true);
            setError('');
            setIssues([]);
            setPreview(await onPreview(text));
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        try {
            setBusy(true);
            setError('');
            setIssues([]);
            setResult(await onSubmit(text));
        } catch (e: any) {
            const rejection = rejectionFromError(e);
            if (rejection) {
                setIssues(rejection.issues);
                setError('');
            } else {
                setError(e?.message || String(e));
            }
        } finally {
            setBusy(false);
        }
    };

    const blocked = !!preview && preview.issues.length > 0;
    const canSubmit = !!text.trim() && !busy && !result && !blocked && !issues.length;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {helperText}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    value={text}
                    onChange={(e) => {
                        setText(e.target.value);
                        setPreview(null);
                        setIssues([]);
                    }}
                    onBlur={check}
                    placeholder={placeholder}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />

                {preview && !blocked && !result && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        {describePreview(preview)} Nothing is saved until you press {actionLabel}.
                    </Alert>
                )}

                <RowIssueList issues={preview?.issues || []} rowsRead={preview?.rowsRead} title={
                    preview && preview.issues.length
                        ? `${preview.issues.length} of ${preview.rowsRead} line(s) can't be read — fix these first`
                        : undefined
                } />
                <RowIssueList issues={issues} />

                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {describeResult(result)}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
                {!preview && !result && (
                    <Tooltip title={text.trim() ? '' : 'Paste at least one line first'}>
                        {/* Disabled MUI buttons swallow pointer events - hence the wrapper. */}
                        <span>
                            <Button onClick={check} disabled={busy || !text.trim()}>
                                Check
                            </Button>
                        </span>
                    </Tooltip>
                )}
                <Tooltip
                    title={
                        canSubmit
                            ? ''
                            : !text.trim()
                            ? 'Paste at least one line first'
                            : blocked || issues.length
                            ? 'Fix the lines listed above first'
                            : 'Press Check first — lines are validated before anything is written'
                    }
                >
                    <span>
                        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
                            {actionLabel}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

// ============================= upload CSV / XLSX =============================

interface UploadRowsModalProps {
    open: boolean;
    title: string;
    helperText: string;
    actionLabel?: string;
    /** Dry run: what the file would do. Nothing is written until the user confirms. */
    onPreview: (rows: string[][]) => Promise<{ rowsRead: number; understood: number; issues: IRowIssue[] }>;
    onClose: () => void;
    onSubmit: (rows: string[][]) => Promise<any>;
}

/**
 * File upload with a mandatory preview.
 *
 * This is how a badly-shaped file gets caught. There's no attempt to validate column headers -
 * spreadsheets arrive in too many layouts for that to be anything but brittle. Instead the
 * server reports how many rows it read and how many it understood, and the import button stays
 * disabled until at least one row parses. A file whose columns are in the wrong order
 * understands nothing, and that's stated plainly rather than importing zero rows and calling
 * it success.
 */
export const UploadRowsModal: React.FC<UploadRowsModalProps> = ({
    open,
    title,
    helperText,
    actionLabel = 'Import',
    onPreview,
    onClose,
    onSubmit,
}) => {
    const [rows, setRows] = useState<string[][]>([]);
    const [fileName, setFileName] = useState('');
    const [preview, setPreview] = useState<{ rowsRead: number; understood: number; issues: IRowIssue[] } | null>(null);
    const [issues, setIssues] = useState<IRowIssue[]>([]);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState('');

    React.useEffect(() => {
        if (open) {
            setRows([]);
            setFileName('');
            setPreview(null);
            setResult(null);
            setError('');
        }
    }, [open]);

    const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setError('');
            setResult(null);
            setPreview(null);
            setFileName(file.name);
            setBusy(true);
            const parsedRows = await readSheetRows(file);
            setRows(parsedRows);
            setPreview(await onPreview(parsedRows));
        } catch (err: any) {
            setError(`Could not read the file: ${err?.message || err}`);
        } finally {
            setBusy(false);
        }
    };

    const submit = async () => {
        try {
            setBusy(true);
            setError('');
            setIssues([]);
            setResult(await onSubmit(rows));
        } catch (e: any) {
            const rejection = rejectionFromError(e);
            if (rejection) setIssues(rejection.issues);
            else setError(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    // Any unreadable row blocks the whole import - partial imports leave the user believing
    // their full list is tracked.
    const blocked = !!preview && preview.issues.length > 0;
    const canSubmit = !!preview && preview.understood > 0 && !blocked && !busy && !result && !issues.length;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {helperText}
                </Typography>

                <Button variant="outlined" component="label" sx={{ mt: 1 }} disabled={busy}>
                    Choose file
                    <input type="file" accept=".csv, .xlsx, .xls" hidden onChange={onFile} />
                </Button>

                {fileName && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {fileName}
                    </Typography>
                )}

                {preview && !blocked && !result && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Read {preview.rowsRead} row(s), all readable. Nothing will be written until you confirm.
                    </Alert>
                )}

                {preview && preview.rowsRead === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        That file has no data rows.
                    </Alert>
                )}

                <RowIssueList
                    issues={preview?.issues || []}
                    rowsRead={preview?.rowsRead}
                    title={
                        preview && preview.issues.length
                            ? `${preview.issues.length} of ${preview.rowsRead} row(s) can't be read — fix these before importing`
                            : undefined
                    }
                />
                <RowIssueList issues={issues} />

                {result && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {result.added !== undefined
                            ? `Added ${result.added}. Skipped ${result.skipped} already present.`
                            : `Removed ${result.removed}. ${result.notFound} lookup(s) matched nothing.`}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
                <Tooltip
                    title={
                        canSubmit
                            ? ''
                            : !rows.length
                            ? 'Choose a file first'
                            : blocked || issues.length
                            ? 'Fix the rows listed above before continuing'
                            : ''
                    }
                >
                    <span>
                        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
                            {actionLabel}
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
};

// ============================= manual check: paste or upload =============================

interface ManualCheckModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (content: string) => Promise<void>;
}

/**
 * Runs a check against file contents supplied by hand, rather than fetching.
 *
 * Accepts a pasted blob or an uploaded .txt - the file is read as plain text, not through the
 * spreadsheet reader, because an app-ads.txt is not a spreadsheet.
 */
export const ManualCheckModal: React.FC<ManualCheckModalProps> = ({ open, onClose, onSubmit }) => {
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    React.useEffect(() => {
        if (open) {
            setText('');
            setError('');
        }
    }, [open]);

    const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setError('');
            setText(await readTextFile(file));
        } catch (err: any) {
            setError(`Could not read the file: ${err?.message || err}`);
        }
    };

    const submit = async () => {
        try {
            setBusy(true);
            setError('');
            await onSubmit(text);
            onClose();
        } catch (e: any) {
            setError(e?.message || String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Add app-ads.txt file manually</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    For publishers that block automated requests. Paste their app-ads.txt exactly as you see it in
                    your browser, or upload a saved copy — the analysis is identical to a fetched check.
                </Typography>

                <Button variant="outlined" component="label" size="small" sx={{ mb: 2 }}>
                    Upload .txt
                    <input type="file" accept=".txt,text/plain" hidden onChange={onFile} />
                </Button>

                <TextField
                    fullWidth
                    multiline
                    minRows={12}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={'pubmatic.com, 161599, DIRECT, 5d62403b186f2ace\nfreewheel.tv, 1576554, DIRECT'}
                    InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !text.trim()}>
                    Run check
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ============================= line change history =============================

export const LineHistoryModal: React.FC<{ open: boolean; line: ITrackedLine | null; onClose: () => void }> = ({
    open,
    line,
    onClose,
}) => (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Change log</DialogTitle>
        <DialogContent dividers>
            {line && (
                <>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }} gutterBottom>
                        {line.domain}, {line.pubId}, {line.relationship}
                        {line.certId ? `, ${line.certId}` : ''}
                    </Typography>
                    {!!(line.demand || line.sourceDescription) && (
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                            Demand: {line.demand || line.sourceDescription}
                        </Typography>
                    )}
                    <Divider sx={{ my: 1 }} />

                    {!(line.statusHistory || []).length ? (
                        <Typography variant="body2" color="text.secondary">
                            {/* Distinguishing "never changed" from "never checked" matters: the first
                                check establishes a baseline and is deliberately not logged as a change. */}
                            {line.lastCheckedAt
                                ? 'No status changes recorded. The first check sets a baseline rather than counting as a change.'
                                : 'This line has never been checked yet.'}
                        </Typography>
                    ) : (
                        <List dense>
                            {(line.statusHistory || []).map((h, i) => (
                                <ListItem key={i} disableGutters>
                                    <ListItemText
                                        primary={`${statusLabel(h.from)} → ${statusLabel(h.to)}`}
                                        secondary={dateToStringFormat(h.date)}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
);

// ============================= updated publisher list =============================

interface UpdateListModalProps {
    open: boolean;
    list: IUpdateList | null;
    onClose: () => void;
    onDownloadCsv: () => void;
}

export const UpdateListModal: React.FC<UpdateListModalProps> = ({ open, list, onClose, onDownloadCsv }) => {
    const section = (
        title: string,
        lines: ITrackedLine[],
        note?: (l: ITrackedLine) => string,
        emptyNote?: string,
    ) => (
        <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
                {title} ({lines.length})
            </Typography>
            {!lines.length ? (
                <Typography variant="body2" color="text.secondary">
                    {emptyNote || 'None.'}
                </Typography>
            ) : (
                <Box sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                    {lines.map((l) => (
                        <div key={l._id}>
                            {l.domain}, {l.pubId}, {l.relationship}
                            {l.certId ? `, ${l.certId}` : ''}
                            {note ? ` — ${note(l)}` : ''}
                            {/* Context for whoever is reviewing the list. Deliberately not in
                                copy() below, which is a terse instruction list. */}
                            {l.demand || l.sourceDescription ? (
                                <span style={{ opacity: 0.6 }}> [{l.demand || l.sourceDescription}]</span>
                            ) : null}
                        </div>
                    ))}
                </Box>
            )}
        </Box>
    );

    const copy = () => {
        if (!list) return;
        const lines = [
            ...list.toAdd.map((l) => `ADD: ${l.domain}, ${l.pubId}, ${l.relationship}${l.certId ? `, ${l.certId}` : ''}`),
            ...list.toCorrect.map(
                (l) =>
                    `CORRECT to ${l.relationship} (currently ${l.actualRelationship || '?'}): ${l.domain}, ${l.pubId}`,
            ),
            ...list.toRemove.map((l) => `REMOVE: ${l.domain}, ${l.pubId}, ${l.relationship}`),
        ];
        navigator.clipboard.writeText(lines.join('\n'));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Updated publisher list{list ? ` — ${list.publisher.name}` : ''}</DialogTitle>
            <DialogContent dividers>
                {/* This reads the last check rather than running one, so say so plainly - a line
                    removed since the last check can't yet be known to be live or gone. */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>This is a request list to send the publisher — it changes nothing by itself.</strong> We
                    have no write access to their app-ads.txt; they have to make these edits. Copy or download it and
                    send it to their ad-ops contact
                    {list?.publisher.contactEmail ? ` (${list.publisher.contactEmail})` : ''}.
                    <br />
                    Built from the most recent check
                    {list?.publisher.lastCheckedAt
                        ? ` (${dateToStringFormat(list.publisher.lastCheckedAt)})`
                        : ' — this publisher has never been checked'}
                    ; generating it doesn't re-read their file.
                </Alert>

                {list?.hasStaleWarning && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {list.staleRemoved.length} removed line(s) haven't been checked since being removed, so they're
                        left out of "lines to remove" rather than guessed at. Run a check to resolve them.
                    </Alert>
                )}

                {list && (
                    <>
                        {section('Lines to add', list.toAdd)}
                        {section('Lines to correct', list.toCorrect, (l) => `currently live as ${l.actualRelationship || '?'}`)}
                        {/* A zero here alongside outstanding removal requests looks broken unless we
                            say why: nothing is confirmed still live until a check has run since removal. */}
                        {section(
                            'Lines to remove',
                            list.toRemove,
                            undefined,
                            list.staleRemoved.length
                                ? `None confirmed yet — ${list.staleRemoved.length} old line(s) are waiting on a check (listed below). Run "Check now" to resolve them.`
                                : 'None.',
                        )}
                        {!!list.staleRemoved.length && section('Old lines, not yet re-checked', list.staleRemoved)}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={copy}>Copy</Button>
                <Button onClick={onDownloadCsv}>Download CSV</Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
